import React, { useState, useMemo } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { CreateAreaPayload } from 'store/areas';
import { getAreaInitialForm, areaValidationSchema, getHasEmptyInput, getHasInvalidInput } from '../../utils';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants';
import { ConfigCreateFormProps } from '../../types';
import { getSortedList } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { selectors as venuesSelectors, Venue } from 'store/venues';

export const CreateAreaForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  // FOR venue
  const venues = useSelector(venuesSelectors.selectVenues);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getAreaInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateAreaPayload = {
      name: form.name,
      description: form.description,
      venueId: form.venueId
    };
    onSubmit(payload);
  };

  const onInputChange = (key: keyof ReturnType<typeof getAreaInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getAreaInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await areaValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getAreaInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  // for venue selection
  const onVenueChange = (item: Venue) => {
    setSelectedVenue(item);
    setForm({ ...form, venueId: item.id });
  };

  return (
    <FlexBox justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Area</Typography>
          </Box>

          <MuiBox className={classes.createClientName}>
            <InputField
              label="name"
              size="medium"
              fullWidth
              placeholder="name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
            />
          </MuiBox>
          <MuiBox className={classes.createClientName}>
            <InputField
              label="description"
              size="medium"
              fullWidth
              placeholder="description"
              value={form.description}
              onChange={(e) => onInputChange('description', e)}
              onBlur={(e) => onInputBlur('description', e, e.target.value.trim())}
              helperText={validate('description')}
              error={validation.description === INVALID}
            />
          </MuiBox>
          <FlexBox fullWidth direction="row" margin="3 0 6" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Venue
            </Typography>
            <SelectDropdown
              placeholder="Select a Venue"
              items={getSortedList(venues, 'name')}
              selectedItem={selectedVenue as Venue}
              onChange={(item: Venue) => onVenueChange(item)}
              itemToString={(item: Venue) => (item ? item.name : '')}
            />
          </FlexBox>
        </Grid>

        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Area
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
